import { theme } from '@theme';
import { Dispatch, FC, SetStateAction, useRef, useState } from 'react';
import styled from 'styled-components';
import { useOutsideClickDetection } from '../../../../hooks/UseOutsideClickDetection';
import { findClosestGreaterValue } from '../../../../services/utils/HelperFunctions';
import { Icon, Typography } from '../../../style';

const dropdownValues = [10, 25, 50, 100];

type PaginationProps = {
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  count: number;
  isHeader?: boolean;
};

const PageSizeDropdown: FC<PaginationProps> = (props) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useOutsideClickDetection(wrapperRef, setIsVisible);

  const handleChange = (item: number) => {
    props.setPageSize(item);
    props.setCurrentPage(1);
  };

  const getClassNameForOptions = (dropdownValue: number) => {
    const closetNumber = findClosestGreaterValue(dropdownValues, props.count);
    return closetNumber < dropdownValue ? 'dropdown-disable' : '';
  };

  const generateOptions = () => {
    return dropdownValues.map((item: number) => (
      <OptionItem
        key={item}
        onClick={() => handleChange(item)}
        className={getClassNameForOptions(item)}
      >
        <Icon
          type='checkmark'
          size={theme.typography.h4}
          visibilty={props.pageSize === item ? 'visible' : 'hidden'}
        ></Icon>
        <Typography size={theme.typography.h5}>{item}</Typography>
      </OptionItem>
    ));
  };

  return (
    <>
      <DropdownWrapper
        onClick={() => setIsVisible(!isVisible)}
        ref={wrapperRef}
        data-testid='pageSize-dropdown'
        id='pageSize-dropdown-options'
      >
        <Typography
          size={theme.typography.h5}
          fontWeight={theme.typography.fontWeights.bold}
          className='-selection'
        >
          {props.pageSize}
        </Typography>
        {isVisible && (
          <DropdownOptions isHeader={props.isHeader}>{generateOptions()}</DropdownOptions>
        )}
        <Icon
          type={'angle-down'}
          size={theme.typography.h4}
          color={theme.palette.wetAsphalt}
          className='-icon'
        ></Icon>
      </DropdownWrapper>
    </>
  );
};

export default PageSizeDropdown;

const OptionItem = styled.div`
  padding: 7px 20px;
  display: flex;
  justify-content: space-between;
`;

const DropdownWrapper = styled.span`
  background-color: white;
  display: flex;
  border: 1px solid #ddd;
  border-radius: 3px;
  position: relative;
  margin-right: 0.5em;
  padding-left: 0.75em;
  cursor: pointer;
  min-width: 40px;
  margin-left: 0.5rem;

  .-icon {
    text-align: right;
    height: 100%;
    display: inline-flex;
    align-items: center;
    margin-top: 0px;

    :before {
      vertical-align: middle;
      padding-right: 0.75em;
      padding-left: 0.75em;
    }
  }

  .-selection {
    padding-top: 0.3em;
  }
`;

type DropdownOptionsProps = {
  isHeader?: boolean;
};

const DropdownOptions = styled.div<DropdownOptionsProps>`
  position: absolute;
  top: ${(props) =>
    props.isHeader !== null && props.isHeader !== undefined && props.isHeader ? '30px' : '-140px'};
  border-radius: 3px;
  z-index: 100;
  background-color: white;
  width: 100%;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
  overflow: auto;
  left: 0;

  & div {
    padding-left: 12px;
  }
  & div:hover {
    background-color: ${(props) => props.theme.palette.darkGray}!important;
    color: white !important;
  }
  & div:hover span {
    color: white;
  }

  .dropdown-disable {
    pointer-events: none;
    cursor: not-allowed;
    background-color: ${(props) => props.theme.palette.gray}!important;
    span {
      color: ${(props) => props.theme.palette.lightGreyTwo}!important;
    }
  }
`;
